import { usePopover } from '@/hooks/usePopover';
import { IconButton, SxProps, Theme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import React, { useState } from 'react';

const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(56, 119, 85, 0.7);
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(56, 119, 85, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(56, 119, 85, 0);
  }
`;

const StyledIconButton = styled(IconButton, {
  shouldForwardProp: property => property !== 'enableShadow',
})<{ enableShadow?: boolean }>(({ enableShadow, theme }) => ({
  width: '26px',
  height: '26px',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  transition: 'all 0.2s ease-in-out',
  transform: 'translateY(0)',
  boxShadow: enableShadow ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none',
  '&:hover': {
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
}));

export const useIconButton = ({ enablePulse, enableShadow }: { enablePulse?: boolean; enableShadow?: boolean }) => {
  const [isPulseEnabled, setIsPulseEnabled] = useState(enablePulse);
  const { open, close, isOpen, anchorElement } = usePopover();
  const pulseSx = enablePulse ? { animation: `${pulseAnimation} 2s infinite` } : {};
  const shadowSx = enableShadow ? { boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' } : {};

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setIsPulseEnabled(false);
    open(event);
  };

  return {
    pulseSx,
    shadowSx,
    isPulseEnabled,
    handleClick,
    open,
    close,
    isOpen,
    anchorElement,
  };
};

export const ActionIconButton: React.FC<{
  enablePulse?: boolean;
  enableShadow?: boolean;
  children: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  sx?: SxProps<Theme>;
  disabled?: boolean;
  'data-testid'?: string;
}> = ({ enablePulse, enableShadow, children, onClick, sx, disabled, 'data-testid': dataTestId }) => {
  const { handleClick, pulseSx } = useIconButton({ enablePulse, enableShadow });
  const [clicked, setClicked] = useState(false);

  return (
    <StyledIconButton
      enableShadow={enableShadow ?? (clicked && enablePulse)}
      onClick={event => {
        setClicked(true);
        onClick?.(event);
        handleClick(event);
      }}
      sx={{
        ...(!clicked && pulseSx),
        ...sx,
      }}
      disabled={disabled}
      data-testid={dataTestId}
    >
      {children}
    </StyledIconButton>
  );
};
